import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { BehaviorSubject, Observable, of, Subject, Subscribable, Subscription } from 'rxjs';
import { map, filter, take, catchError, switchMap } from 'rxjs/operators';

import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';

import { ActivityBlockingService } from '../services/activity-blocking.service';
import { BrowserProviderService } from '../services/browser-provider.service';
import { LoggingService } from '../services/logging.service';

import { SharedSettings } from '../models/shared-settings';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private roles: BehaviorSubject<string[]>;

    private authMessage: BehaviorSubject<{ summary: string, detail: string}>;

    private errState = false;

    private isReady = new BehaviorSubject<boolean>(false);

    get Roles(): Observable<string[]> {
        return this.roles.asObservable().pipe(filter(roles => roles && roles.length > 0));
    }

    get AuthMessage(): Observable<{ summary: string, detail: string}> {
        return this.authMessage.asObservable();
    }

    private _loaded = new Subject<void>();

    constructor(
            private msal: MsalService,
            private broadcastService: MsalBroadcastService,
            private http: HttpClient,
            private logger: LoggingService,
            private browser: BrowserProviderService,
            private activityService: ActivityBlockingService,
            private settings: SharedSettings) {
        this.roles = new BehaviorSubject<string[]>([]);
        this.authMessage = new BehaviorSubject<{ summary: string, detail: string}>(null);

        this.broadcastService.msalSubject$.subscribe(event => {
            if (event && (!this.settings.isProduction || event.error))
                console.warn(event);
        });

        this.msal.handleRedirectObservable().subscribe(_ => {
            this.isReady.next(true);
            this.isAuthenticated().pipe(take(1)).subscribe(isAuthenticated => {
                if (isAuthenticated) {
                    this.http.get<string[]>(this.settings.apiBaseUrl + 'users/me/roles').subscribe({
                        next: roles => {
                            this.roles.next(roles);
                        },
                        error: _error => {
                            this.roles.next([]);
                        },
                        complete: () => {
                            this._loaded.next();
                        }
                    });
                }
            });
        });
    }

    logout(): void {
        this.http.post(this.settings.apiBaseUrl + 'logout', {})
            .pipe(
                catchError(_ => of({})),
                switchMap(() => this.msal.logout())
            )
            .subscribe();
    }

    getAccount(): Observable<AccountInfo> {
        return this.isReady.pipe(
            filter(is => is),
            take(1),
            map(_ => {
                try {
                    const accounts = this.msal.instance.getAllAccounts();

                    // not logged in
                    if (!accounts.length) {
                        if (!this.errState) {
                            this.errState = true;
                            this.activityService.StartWorking();
                            this.msal.loginRedirect({
                                redirectStartPage: this.browser.GetLocation().href,
                                scopes: this.settings.authScopes
                            });
                        }

                        return null;
                    }
                    // non-company account
                    else if (!this.settings.validAccountDomains.includes(accounts[0].username.toLowerCase().split('@').at(-1))) {
                        if (!this.errState) {
                            this.errState = true;
                            this.activityService.StartWorking();
                            this.authMessage.next({
                                summary: 'Invalid Account',
                                detail: 'PLA Vision may only be accessed via PLA accounts. '
                                    + 'You will be signed out and asked to sign in again.',
                            });
                            setTimeout(() => this.msal.logout(), 5000);
                        }

                        return null;
                    }
                    // all ok
                    else {
                        return accounts[0];
                    }
                }
                catch (e) {
                    this.logger.warning('Auth Error', { msg: 'error getting user from msal', error: e });
                }

                return null;
            }));
    }

    getUsername(): Observable<string> {
        return this.getAccount().pipe(map(acct => acct?.username));
    }

    getDisplayName(): Observable<string> {
        return this.getAccount().pipe(map(acct => acct?.name));
    }

    getUserRoles(): string[] {
        return this.roles.value;
    }

    isUserInAnyRoles(roles: string[]): Observable<boolean> {
        if (!roles || roles.length === 0)
            return of(true);

        return this.roles.pipe(
            filter(userRoles => userRoles && userRoles.length > 0),
            take(1),
            map(userRoles => roles.some(r => userRoles.some(ur => ur === r)))
        );
    }

    isAuthenticated(): Observable<boolean> {
        return this.getAccount().pipe(map(acct => !!acct));
    }

    forceLogin(): void {
        this.msal.loginRedirect();
    }
}
